import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function EditEditorTask() {
  const [formData, setFormData] = useState({
    video_name: '',
    client_id: '',
    shoot_date: '',
    raw_link: '',
    notes: '',
    status: 'pending',
    video_type: 'indoor_reel',
    video_count: 1
  });
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingClients, setFetchingClients] = useState(true);
  const [fetchingTask, setFetchingTask] = useState(true);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { id } = useParams();


  // Colors
  const colors = {
    backgroundDark: '#0f0c29',
    backgroundMedium: '#302b63',
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    inputBg: 'rgba(255, 255, 255, 0.07)',
    selectBg: 'rgba(255, 255, 255, 0.07)',
    optionBg: '#24243e'
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    header: {
      padding: '30px 20px 20px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    backBtn: {
      background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '10px 20px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    content: {
      padding: '0 20px 40px',
      maxWidth: '800px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '25px',
      borderBottom: `1px solid ${colors.borderDark}`
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      letterSpacing: '0.5px'
    },
    form: {
      padding: '35px'
    },
    formGroup: {
      marginBottom: '30px'
    },
    label: {
      display: 'block',
      color: colors.textLight,
      marginBottom: '10px',
      fontWeight: '600',
      fontSize: '0.95rem',
      letterSpacing: '0.3px'
    },
    required: {
      color: colors.danger,
      marginLeft: '3px',
      fontSize: '1.1em'
    },
    input: {
      width: '100%',
      padding: '14px 18px',
      background: colors.inputBg,
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      color: 'white',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    select: {
      width: '100%',
      padding: '14px 18px',
      background: colors.selectBg,
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      color: 'white',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 18px center',
      backgroundSize: '14px',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    textarea: {
      width: '100%',
      padding: '14px 18px',
      background: colors.inputBg,
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      color: 'white',
      fontSize: '0.95rem',
      minHeight: '120px',
      resize: 'vertical',
      transition: 'all 0.3s ease',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    error: {
      color: colors.danger,
      fontSize: '0.85rem',
      marginTop: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '500'
    },
    buttonGroup: {
      display: 'flex',
      gap: '20px',
      marginTop: '40px',
      paddingTop: '25px',
      borderTop: `1px solid ${colors.borderDark}`
    },
    submitBtn: {
      background: `linear-gradient(135deg, ${colors.success}, #059669)`,
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      flex: 1,
      letterSpacing: '0.5px',
      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
    },
    cancelBtn: {
      background: `linear-gradient(135deg, ${colors.inputBg}, rgba(255, 255, 255, 0.1))`,
      color: colors.textLight,
      border: `2px solid ${colors.borderDark}`,
      padding: '15px 30px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      flex: 1,
      letterSpacing: '0.5px'
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      color: colors.textLight,
      fontSize: '0.9rem'
    },
    optional: {
      color: colors.textLight,
      fontSize: '0.8rem',
      fontStyle: 'italic',
      marginLeft: '6px',
      opacity: 0.7
    },
    messageAlert: {
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px',
      margin: '0 auto 25px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '800px',
      width: '100%',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
    },
    spinner: {
      width: '16px',
      height: '16px',
      border: '2px solid transparent',
      borderTop: `2px solid ${colors.textLight}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  // Video type options as specified
  const videoTypeOptions = [
    { value: 'indoor_reel', label: 'Indoor Reel' },
    { value: 'outdoor_reel', label: 'Outdoor Reel' },
    { value: 'podcast', label: 'Podcast' },
    { value: 'indoor_long_video', label: 'Indoor Long Video' },
    { value: 'outdoor_long_video', label: 'Outdoor Long Video' }
  ];

  // Status options
  const statusOptions = [
    { value: 'todo', label: 'To Do' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'needs_revision', label: 'Needs Revision' }
  ];

  // Fetch clients and task data on component mount
  useEffect(() => {
    fetchClients();
    if (id) {
      fetchTaskData(id);
    } else {
      setFetchingTask(false);
      setMessage('❌ No task ID provided');
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      setFetchingClients(true);
      const response = await fetch(`${API_BASE}/get_clients.php?type=clients`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle both response formats
      if (Array.isArray(data)) {
        setClients(data);
      } else if (data.success && Array.isArray(data.clients)) {
        setClients(data.clients);
      } else if (data.clients && Array.isArray(data.clients)) {
        setClients(data.clients);
      } else {
        console.warn('Unexpected API response format:', data);
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setMessage(`⚠️ Could not load clients: ${error.message}`);
      setClients([]);
    } finally {
      setFetchingClients(false);
    }
  };

  const fetchTaskData = async (taskId) => {
    try {
      setFetchingTask(true);
      setMessage('');
      
      const response = await fetch(`${API_BASE}/get_task.php?id=${taskId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.task) {
        const task = data.task;
        
        // Format shoot_date for input field (YYYY-MM-DD)
        let formattedShootDate = '';
        if (task.shoot_date) {
          const date = new Date(task.shoot_date);
          formattedShootDate = date.toISOString().split('T')[0];
        }
        
        setFormData({
          video_name: task.video_name || '',
          client_id: task.client_id || '',
          shoot_date: formattedShootDate,
          raw_link: task.raw_link || '',
          notes: task.notes || '',
          status: task.status || 'pending',
          video_type: task.video_type || 'indoor_reel',
          video_count: task.video_count || 1
        });
      } else {
        throw new Error(data.message || 'Failed to fetch task data');
      }
    } catch (error) {
      console.error('Error fetching task data:', error);
      setMessage(`❌ Error loading task: ${error.message}`);
    } finally {
      setFetchingTask(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.video_name.trim()) {
      newErrors.video_name = 'Video name is required';
    }
    
    if (!formData.raw_link.trim()) {
      newErrors.raw_link = 'Video link is required';
    } else if (!isValidUrl(formData.raw_link)) {
      newErrors.raw_link = 'Please enter a valid URL (starting with http:// or https://)';
    }
    
    if (!formData.video_count || formData.video_count < 1) {
      newErrors.video_count = 'Video count must be at least 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    setMessage('❌ Please fix the errors in the form');
    return;
  }
  
  setSaving(true);
  setMessage('');
  
  try {
    const userId = sessionStorage.getItem('userId');
    
    // Prepare task data for update - make sure all fields are included
    const taskData = {
      id: id,
      video_name: formData.video_name.trim(),
      raw_link: formData.raw_link.trim(),
      video_type: formData.video_type,
      video_count: parseInt(formData.video_count) || 1,
      status: formData.status,
      operator_id: userId || null
    };
    
    // Add optional fields
    if (formData.client_id && formData.client_id !== '') {
      taskData.client_id = formData.client_id;
    } else {
      taskData.client_id = '';
    }
    
    if (formData.shoot_date && formData.shoot_date !== '') {
      taskData.shoot_date = formData.shoot_date;
    } else {
      taskData.shoot_date = '';
    }
    
    if (formData.notes && formData.notes.trim() !== '') {
      taskData.notes = formData.notes.trim();
    } else {
      taskData.notes = '';
    }
    
    // Log the data being sent
    console.log('Task data to send:', taskData);
    console.log('JSON string:', JSON.stringify(taskData));
    
    // Test with a simpler approach first
    const testData = {
      id: id,
      video_name: 'Test Video',
      raw_link: 'https://example.com/test.mp4',
      video_type: 'indoor_reel',
      video_count: 1,
      status: 'pending',
      operator_id: userId || 1
    };
    
    console.log('Test data:', testData);
    
    // Try with simpler data first
    const response = await fetch(`${API_BASE}/edit_task.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(testData) // Use testData first
    });
    
    console.log('Response status:', response.status);
    
    // First get the response as text
    const responseText = await response.text();
    console.log('Raw response text:', responseText);
    
    try {
      // Try to parse as JSON
      const data = JSON.parse(responseText);
      console.log('Parsed response:', data);
      
      if (data.success) {
        // If test works, try with actual data
        const actualResponse = await fetch(`${API_BASE}/edit_task.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(taskData)
        });
        
        const actualText = await actualResponse.text();
        const actualData = JSON.parse(actualText);
        
        if (actualData.success) {
          setMessage('✅ Task updated successfully! Redirecting...');
          setTimeout(() => {
            navigate('');
          }, 1500);
        } else {
          throw new Error(actualData.message || 'Failed to update task');
        }
      } else {
        throw new Error(data.message || 'Test failed');
      }
    } catch (parseError) {
      console.error('Failed to parse response as JSON:', parseError);
      console.error('Response was:', responseText);
      throw new Error(`Server returned invalid response. Status: ${response.status}`);
    }
    
  } catch (error) {
    console.error('Error updating task:', error);
    setMessage(`❌ Error: ${error.message}`);
  } finally {
    setSaving(false);
  }
};
  return (
    <Layout>
      <div style={styles.container}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                <button 
                  style={styles.backBtn}
                  onClick={() => navigate('/dashboard')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary}30, ${colors.secondary}30)`;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back to Tasks Pool
                </button>
                <h1 style={styles.pageTitle}>
                  <i className="fas fa-edit"></i>
                  Edit Task #{id}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? `linear-gradient(135deg, ${colors.success}20, ${colors.success}10)` 
              : message.includes('⚠️')
              ? `linear-gradient(135deg, ${colors.warning}20, ${colors.warning}10)`
              : `linear-gradient(135deg, ${colors.danger}20, ${colors.danger}10)`,
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : message.includes('⚠️')
              ? `1px solid ${colors.warning}`
              : `1px solid ${colors.danger}`,
            maxWidth: '800px'
          }}>
            <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : message.includes('⚠️') ? 'fa-exclamation-triangle' : 'fa-times-circle'}`} 
               style={{
                 fontSize: '1rem',
                 color: message.includes('✅') ? colors.success : message.includes('⚠️') ? colors.warning : colors.danger
               }}></i>
            <span style={{flex: 1, fontWeight: '500'}}>{message}</span>
            <button onClick={() => setMessage('')} style={{
              background: 'none', 
              border: 'none', 
              color: colors.danger, 
              cursor: 'pointer', 
              padding: '5px',
              borderRadius: '5px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
              <i className="fas fa-times" style={{fontSize: '0.9rem'}}></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div style={styles.content}>
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-tasks"></i>
                Edit Task Details
              </h3>
              <p style={{margin: '8px 0 0 0', opacity: 0.9, fontSize: '0.9rem'}}>
                Update the task details below
              </p>
            </div>
            
            {fetchingTask ? (
              <div style={{padding: '50px', textAlign: 'center'}}>
                <div style={styles.spinner}></div>
                <p style={{color: colors.textLight, marginTop: '15px'}}>Loading task data...</p>
              </div>
            ) : (
              <form style={styles.form} onSubmit={handleSubmit}>
                {/* Video Name - Required */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Video Name <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="video_name"
                    value={formData.video_name}
                    onChange={handleChange}
                    placeholder="Enter video name (e.g., Product Launch Video)"
                    style={{
                      ...styles.input,
                      borderColor: errors.video_name ? colors.danger : colors.borderDark
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.video_name ? colors.danger : colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${errors.video_name ? colors.danger + '20' : colors.primary + '20'}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.video_name ? colors.danger : colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.video_name && (
                    <div style={styles.error}>
                      <i className="fas fa-exclamation-circle" style={{fontSize: '0.8rem'}}></i>
                      {errors.video_name}
                    </div>
                  )}
                </div>

                {/* Client Selection - Optional */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Client <span style={styles.optional}>(Optional)</span>
                  </label>
                  <select
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleChange}
                    style={{
                      ...styles.select,
                      backgroundColor: colors.selectBg,
                      borderColor: errors.client_id ? colors.danger : colors.borderDark
                    }}
                    disabled={fetchingClients}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="" style={{backgroundColor: colors.optionBg}}>Select a client</option>
                    {fetchingClients ? (
                      <option value="" style={{backgroundColor: colors.optionBg}} disabled>
                        Loading clients...
                      </option>
                    ) : clients.length === 0 ? (
                      <option value="" style={{backgroundColor: colors.optionBg}} disabled>
                        No clients available
                      </option>
                    ) : (
                      clients.map(client => (
                        <option 
                          key={client.id} 
                          value={client.id}
                          style={{backgroundColor: colors.optionBg, color: 'white'}}
                        >
                          {client.name}
                        </option>
                      ))
                    )}
                  </select>
                  {fetchingClients && (
                    <div style={{marginTop: '8px', color: colors.textLight, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <div style={styles.spinner}></div>
                      Loading clients...
                    </div>
                  )}
                </div>

                {/* Shoot Date - Optional */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Shoot Date <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="date"
                    name="shoot_date"
                    value={formData.shoot_date}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      colorScheme: 'dark'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Raw Video Link - Required */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Video Link <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="url"
                    name="raw_link"
                    value={formData.raw_link}
                    onChange={handleChange}
                    placeholder="https://drive.google.com/file/d/... or https://vimeo.com/..."
                    style={{
                      ...styles.input,
                      borderColor: errors.raw_link ? colors.danger : colors.borderDark
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.raw_link ? colors.danger : colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${errors.raw_link ? colors.danger + '20' : colors.primary + '20'}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.raw_link ? colors.danger : colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.raw_link && (
                    <div style={styles.error}>
                      <i className="fas fa-exclamation-circle" style={{fontSize: '0.8rem'}}></i>
                      {errors.raw_link}
                    </div>
                  )}
                  <div style={{marginTop: '8px', color: colors.textLight, fontSize: '0.85rem', opacity: 0.7}}>
                    <i className="fas fa-info-circle" style={{marginRight: '6px'}}></i>
                    Enter Google Drive, Dropbox, Vimeo, or YouTube link
                  </div>
                </div>

                {/* Video Type - Required */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Video Type <span style={styles.required}>*</span>
                  </label>
                  <select
                    name="video_type"
                    value={formData.video_type}
                    onChange={handleChange}
                    style={{
                      ...styles.select,
                      backgroundColor: colors.selectBg
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {videoTypeOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        style={{backgroundColor: colors.optionBg, color: 'white'}}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status - Required */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Status <span style={styles.required}>*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    style={{
                      ...styles.select,
                      backgroundColor: colors.selectBg
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {statusOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        style={{backgroundColor: colors.optionBg, color: 'white'}}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Video Count - Required */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Video Count <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="number"
                    name="video_count"
                    value={formData.video_count}
                    onChange={handleChange}
                    min="1"
                    style={{
                      ...styles.input,
                      borderColor: errors.video_count ? colors.danger : colors.borderDark
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.video_count ? colors.danger : colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${errors.video_count ? colors.danger + '20' : colors.primary + '20'}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.video_count ? colors.danger : colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.video_count && (
                    <div style={styles.error}>
                      <i className="fas fa-exclamation-circle" style={{fontSize: '0.8rem'}}></i>
                      {errors.video_count}
                    </div>
                  )}
                </div>

                {/* Notes - Optional */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Notes <span style={styles.optional}>(Optional)</span>
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Add any additional notes, instructions, or requirements..."
                    style={styles.textarea}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderDark;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Form Actions */}
                <div style={styles.buttonGroup}>
                  <button
                    type="submit"
                    style={styles.submitBtn}
                    disabled={saving}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.3)';
                      }
                    }}
                  >
                    {saving ? (
                      <>
                        <div style={styles.spinner}></div>
                        Updating Task...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        Update Task
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    style={styles.cancelBtn}
                    onClick={() => navigate('/editor-tasks-pool')}
                    disabled={saving}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.currentTarget.style.background = `linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.12))`;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${colors.inputBg}, rgba(255, 255, 255, 0.1))`;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Style for dropdown options */
        select option {
          background-color: ${colors.optionBg};
          color: white;
          padding: 12px;
        }
        
        select option:hover {
          background-color: ${colors.primary};
        }
        
        select option:checked {
          background-color: ${colors.primary};
          color: white;
        }
        
        /* Improve date input styling */
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
          opacity: 0.7;
          cursor: pointer;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
        }
        
        /* Number input spinner styling */
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          opacity: 0.7;
        }
        
        input[type="number"]::-webkit-inner-spin-button:hover,
        input[type="number"]::-webkit-outer-spin-button:hover {
          opacity: 1;
        }
      `}</style>
    </Layout>
  );
}

export default EditEditorTask;